import { useMemo, useState } from "react";
import PhoneMockup from "./PhoneMockup";
import ProductVideo from "./ProductVideo";
import { products, categories } from "@/mocks/products";
import { track } from "@/lib/analytics";

export default function ProductDemo() {
  const [category, setCategory] = useState("Todos");
  const [active, setActive] = useState(0);

  const filtered = useMemo(
    () =>
      category === "Todos"
        ? products
        : products.filter((p) => p.category === category),
    [category]
  );

  const current = filtered[active % Math.max(filtered.length, 1)];

  const goTo = (dir: number) => {
    if (filtered.length === 0) return;
    setActive((a) => (a + dir + filtered.length) % filtered.length);
  };

  const selectCategory = (cat: string) => {
    setCategory(cat);
    setActive(0);
  };

  return (
    <section
      id="demo"
      className="grain relative overflow-hidden bg-foreground-950 py-24 md:py-32"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,oklch(var(--primary-500)/0.16),transparent_60%)]" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6">
        <div className="reveal mx-auto max-w-3xl text-center">
          <span className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent-500">
            <span className="h-px w-6 bg-current" />
            Demonstração
            <span className="h-px w-6 bg-current" />
          </span>
          <h2 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight text-foreground-50 md:text-6xl">
            Não imagine.
            <br />
            <span className="text-primary-500">Experimente.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-foreground-300 md:text-lg">
            Navegue pelos pratos, mude de categoria e sinta como seu cliente vai
            descobrir — e desejar — o seu cardápio.
          </p>
        </div>

        {/* Categorias */}
        <div className="reveal mt-12 flex flex-wrap justify-center gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => selectCategory(cat)}
              className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                category === cat
                  ? "bg-primary-500 text-foreground-950"
                  : "border border-foreground-700/60 bg-secondary-500/25 text-foreground-300 hover:border-foreground-500 hover:text-foreground-100"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Aparelho grande */}
        <div className="mt-14 flex flex-col items-center">
          <div className="relative mx-auto w-full max-w-[340px] sm:max-w-[380px]">
            <div className="absolute inset-0 -z-10 scale-125 rounded-full bg-primary-500/20 blur-3xl" />
            <PhoneMockup>
              {current && (
                <div key={`${category}-${current.id}`} className="absolute inset-0">
                  <ProductVideo
                    src={current.video}
                    poster={current.image}
                    alt={current.name}
                  />
                  <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-foreground-950/95 via-foreground-950/40 to-transparent p-5 pt-20">
                    <span className="inline-block rounded-full bg-primary-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-foreground-950">
                      {current.category}
                    </span>
                    <h3 className="mt-2 font-heading text-2xl font-bold text-foreground-50">
                      {current.name}
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-300">
                      {current.description}
                    </p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xl font-bold text-primary-500">
                        {current.price}
                      </span>
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-foreground-50 text-foreground-950">
                        <i className="ri-add-line text-xl" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </PhoneMockup>
          </div>

          {/* Controles */}
          <div className="mt-8 flex items-center gap-5">
            <button
              type="button"
              onClick={() => goTo(-1)}
              aria-label="Produto anterior"
              className="flex h-14 w-14 items-center justify-center rounded-full border border-foreground-700/60 bg-secondary-500/30 text-foreground-100 transition-colors hover:border-foreground-500 hover:bg-secondary-500/50"
            >
              <i className="ri-arrow-up-line text-xl" aria-hidden="true" />
            </button>

            <div className="flex gap-1.5">
              {filtered.map((p, i) => (
                <span
                  key={p.id}
                  className={`h-1.5 rounded-full transition-all ${
                    i === active ? "w-7 bg-primary-500" : "w-1.5 bg-foreground-700"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => goTo(1)}
              aria-label="Próximo produto"
              className="flex h-14 w-14 items-center justify-center rounded-full border border-foreground-700/60 bg-secondary-500/30 text-foreground-100 transition-colors hover:border-foreground-500 hover:bg-secondary-500/50"
            >
              <i className="ri-arrow-down-line text-xl" aria-hidden="true" />
            </button>
          </div>

          <p className="mt-4 text-sm italic text-foreground-500">
            Deslize no aparelho — ou use os botões — para mudar de prato.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 flex justify-center">
          <a
            href="#contato"
            onClick={() => track("demo_click", { cta: "product_demo" })}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-9 py-4 text-base font-semibold text-foreground-950 transition-all hover:bg-primary-400 hover:scale-[1.02]"
          >
            Quero meu PlayMenu
            <i className="ri-arrow-right-line ml-2 text-lg" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}